import { useCallback } from "react";
import { Icon, Stack, Text, Tooltip } from "@bookingcom/bui-react";
import { QuestionMarkCircleIcon } from "@bookingcom/bui-assets-react/streamline";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { CampaignImpactKpi, CampaignImpactMetadata, CampaignImpactMetricName } from "../../types";
import { getMetricBoxConfig } from "../../constants";
import styles from "./CampaignImpact.module.css";
import { useAuth } from "../../hooks/useAuth";
import { getUpliftSubtitle, isPercentageMetric, roundKpiValue } from "../../utils/campaignImpact";

type CampaignImpactCardsProps = {
  kpis: CampaignImpactKpi[];
  metadata?: CampaignImpactMetadata;
};

export const CampaignImpactCards = ({ kpis, metadata }: CampaignImpactCardsProps) => {
  const i18n = useI18n();
  const { isAirlineUser } = useAuth();

  const formatValue = useCallback((value: number, name: CampaignImpactMetricName): string => {
    const rounded = roundKpiValue(value, name);
    const sign = rounded > 0 ? "+" : "";
    const valueStr = isPercentageMetric(name) ? `${rounded.toFixed(1)}%` : rounded;

    return `${sign}${valueStr}`;
  }, []);

  return (
    <Stack direction="row" gap={0} alignItems="stretch" divided>
      {getMetricBoxConfig(i18n).map((config) => {
        // If data is not statistically significant, it considers as unavailable for airline users
        const dataAvailable = !isAirlineUser || !!metadata?.statisticallySignificant;

        const kpi = dataAvailable ? kpis.find((k) => k.name === config.metricName)?.value : undefined;
        const formattedValue = typeof kpi === "number" ? formatValue(kpi, config.metricName) : undefined;

        const subtitleKpi =
          dataAvailable && config.subtitleMetricName
            ? kpis.find((k) => k.name === config.subtitleMetricName)?.value
            : undefined;
        const subtitle =
          formattedValue !== undefined && config.subtitleMetricName && typeof subtitleKpi === "number"
            ? getUpliftSubtitle(i18n, config.subtitleMetricName, subtitleKpi)
            : undefined;

        return (
          <Stack direction="column" key={config.metricName} gap={2} grow className={styles.card}>
            <Stack direction="row" alignItems="baseline" gap={2}>
              <Text variant="body_1">{config.label}</Text>
              <Tooltip text={config.tooltip} position="top-start">
                {(attributes) => (
                  <Icon svg={QuestionMarkCircleIcon} size="small" color="action" attributes={attributes} />
                )}
              </Tooltip>
            </Stack>

            <Stack direction="column" gap={1}>
              <div className={styles.valueContainer}>
                {formattedValue ? (
                  <Text variant="headline_1">{formattedValue}</Text>
                ) : (
                  <Text variant="strong_1" color="disabled">
                    {i18n.trans(t("flights_airline_portal_airline_view_campaign_error_data_unavailable_metric_status"))}
                  </Text>
                )}
              </div>
              {subtitle && (
                <Text variant="small_1" color="constructive">
                  {subtitle}
                </Text>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};
